import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

export const EMAIL_VERIFICATION_STATUSES = {
  user_not_found: "user_not_found",
  existing_member: "existing_member",
  new_member: "new_member",
  user_with_pending_invitation: "user_with_pending_invitation",
} as const;

export type EmailVerificationStatus =
  (typeof EMAIL_VERIFICATION_STATUSES)[keyof typeof EMAIL_VERIFICATION_STATUSES];

export const EMAIL_VERIFICATION_STEPS = {
  initial: "initial",
  validating: "validating",
  otp_required: "otp_required",
  password_signin: "password_signin",
  signup: "signup",
  claim: "claim",
} as const;

export type EmailVerificationStep =
  (typeof EMAIL_VERIFICATION_STEPS)[keyof typeof EMAIL_VERIFICATION_STEPS];

export const EMAIL_VERIFICATION_ACTION_TYPES = {
  SET_EMAIL: "SET_EMAIL",
  START_VALIDATION: "START_VALIDATION",
  VALIDATION_SUCCESS: "VALIDATION_SUCCESS",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  START_OTP_SUBMISSION: "START_OTP_SUBMISSION",
  OTP_SUBMISSION_SUCCESS: "OTP_SUBMISSION_SUCCESS",
  OTP_SUBMISSION_ERROR: "OTP_SUBMISSION_ERROR",
  START_RESEND_OTP: "START_RESEND_OTP",
  RESEND_OTP_SUCCESS: "RESEND_OTP_SUCCESS",
  RESEND_OTP_ERROR: "RESEND_OTP_ERROR",
  START_PASSWORD_SUBMISSION: "START_PASSWORD_SUBMISSION",
  PASSWORD_SUBMISSION_SUCCESS: "PASSWORD_SUBMISSION_SUCCESS",
  PASSWORD_SUBMISSION_ERROR: "PASSWORD_SUBMISSION_ERROR",
  START_FORGOT_PASSWORD_SUBMISSION: "START_FORGOT_PASSWORD_SUBMISSION",
  FORGOT_PASSWORD_SUBMISSION_SUCCESS: "FORGOT_PASSWORD_SUBMISSION_SUCCESS",
  FORGOT_PASSWORD_SUBMISSION_ERROR: "FORGOT_PASSWORD_SUBMISSION_ERROR",
  SHOW_OTP_FORM: "SHOW_OTP_FORM",
  SHOW_PASSWORD_FORM: "SHOW_PASSWORD_FORM",
  SHOW_SIGNUP_FORM: "SHOW_SIGNUP_FORM",
  SHOW_CLAIM_FORM: "SHOW_CLAIM_FORM",
  RESET_TO_INITIAL: "RESET_TO_INITIAL",
} as const;

export const EMAIL_VERIFICATION_ERROR_TYPES = {
  required: "required",
  pattern: "pattern",
  manual: "manual",
} as const;

export type EmailVerificationErrorType =
  (typeof EMAIL_VERIFICATION_ERROR_TYPES)[keyof typeof EMAIL_VERIFICATION_ERROR_TYPES];

export interface EmailVerificationState {
  email: string;
  error: string | null;
  isLoading: boolean;
  isResendingOTP: boolean;
  isSubmittingForgotPassword: boolean;
  isSubmittingOTP: boolean;
  isSubmittingPassword: boolean;
  shouldShowAuthenticationLink: boolean;
  status: EmailVerificationStatus | null;
  step: EmailVerificationStep;
  verifiedOTP: string | null;
}

export type EmailVerificationAction =
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.SET_EMAIL; payload: string }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.START_VALIDATION }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.VALIDATION_SUCCESS;
      payload: { status: EmailVerificationStatus };
    }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.VALIDATION_ERROR;
      payload: string;
    }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.START_OTP_SUBMISSION }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_SUCCESS;
      payload?: { otp: string };
    }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_ERROR;
      payload: string;
    }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.START_RESEND_OTP }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_SUCCESS }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_ERROR;
      payload: string;
    }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.START_PASSWORD_SUBMISSION }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_SUCCESS }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_ERROR;
      payload: string;
    }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.START_FORGOT_PASSWORD_SUBMISSION;
    }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_SUCCESS;
    }
  | {
      type: typeof EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_ERROR;
      payload: string;
    }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.SHOW_OTP_FORM }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.SHOW_PASSWORD_FORM }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.SHOW_SIGNUP_FORM }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.SHOW_CLAIM_FORM }
  | { type: typeof EMAIL_VERIFICATION_ACTION_TYPES.RESET_TO_INITIAL };

export interface OTPVerificationResponse {
  checkout_session?: CheckoutSession;
  current_community_name?: string;
  other_community_names?: string[];
  redirect_url?: string;
  status?: EmailVerificationStatus;
}

export interface OTPResendResponse {
  message: string;
}

export interface PasswordSignInResponse {
  checkout_session?: CheckoutSession;
  csrf_token?: string;
  redirect_url?: string;
  success?: boolean;
}

export interface ForgotPasswordResponse {
  message: string;
  redirect_url?: string;
}
